import {
  PHYS_SCALE,
  FIXED_TIMESTEP,
  POSITION_ITERATIONS,
  VELOCITY_ITERATIONS,
  b2AABB,
  b2Body,
  b2BodyDef,
  b2CircleShape,
  b2FixtureDef,
  b2PolygonShape,
  b2Vec2,
  b2World,
} from "./box2d-compat.js";
import { PlayerBox } from "./player-box.js";
import { MyContactListener } from "./contact-listener.js";
import { displayBounds } from "./flash-display-bounds.js";

const LEFT_KEYS = ["ArrowLeft", "KeyA"];
const RIGHT_KEYS = ["ArrowRight", "KeyD"];
const UP_KEYS = ["ArrowUp", "KeyW", "Space"];
const CAMERA_EASE = 1 - Math.pow(2, -10 / 31);
const CHECKPOINT_FRAME_COUNT = 5;
const LEVEL_AIM_FRAME_COUNT = 20;
const DEATH_DELAY_FRAMES = Math.round(1200 / (1000 / 31));
const STOCK_WIN_DELAY_FRAMES = 88;
const FIXED_WIN_DELAY_FRAMES = 89;

function sortedFramePaths(folder, frameCount) {
  return Array.from({ length: frameCount }, (_, index) => `${folder}/${index + 1}.png`);
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function playerFlagBounds(player) {
  const size = player.radius * 2;
  const halfExtent = size / 2 * (Math.abs(Math.cos(player.body.GetAngle())) + Math.abs(Math.sin(player.body.GetAngle())));
  return {
    x: player.x - halfExtent,
    y: player.y - halfExtent,
    width: halfExtent * 2,
    height: halfExtent * 2,
  };
}

function drawStageImage(ctx, image, levelData) {
  const { layout, pngLayout } = levelData;
  if ((levelData.imagePath ?? "").endsWith(".png") && pngLayout) {
    ctx.drawImage(
      image,
      pngLayout.cropX,
      pngLayout.cropY,
      pngLayout.cropWidth,
      pngLayout.cropHeight,
      layout.imageOffsetX,
      layout.imageOffsetY,
      layout.imageWidth,
      layout.imageHeight,
    );
    return;
  }
  ctx.drawImage(image, layout.imageOffsetX, layout.imageOffsetY);
}

function drawSymbol(ctx, image, symbolData, x, y, rotation = 0) {
  if (!image?.complete || !symbolData?.layout) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  if (symbolData.pngLayout) {
    const scaleX = symbolData.layout.imageWidth / symbolData.pngLayout.sourceWidth;
    const scaleY = symbolData.layout.imageHeight / symbolData.pngLayout.sourceHeight;
    ctx.drawImage(
      image,
      symbolData.pngLayout.cropX,
      symbolData.pngLayout.cropY,
      symbolData.pngLayout.cropWidth,
      symbolData.pngLayout.cropHeight,
      symbolData.layout.imageOffsetX + symbolData.pngLayout.cropX * scaleX,
      symbolData.layout.imageOffsetY + symbolData.pngLayout.cropY * scaleY,
      symbolData.pngLayout.cropWidth * scaleX,
      symbolData.pngLayout.cropHeight * scaleY,
    );
  } else {
    ctx.drawImage(image, symbolData.layout.imageOffsetX, symbolData.layout.imageOffsetY);
  }
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbolData, x, y, rotation = 0) {
  if (!image?.complete || !symbolData?.layout) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.drawImage(
    image,
    symbolData.layout.imageOffsetX,
    symbolData.layout.imageOffsetY,
    symbolData.layout.imageWidth,
    symbolData.layout.imageHeight,
  );
  ctx.restore();
}

export class ScriptedLevelRuntime {
  constructor(levelData, clock, config, runtimeState = null, { audioEnabled = true, assetsEnabled = true, fixedLevelTransitionTiming = false } = {}) {
    this.levelData = levelData;
    this.clock = clock;
    this.config = config;
    this.world = new b2World(new b2Vec2(0, 10), true);
    this.contactListener = new MyContactListener();
    this.world.SetContactListener(this.contactListener);

    this.beginFlag = runtimeState?.beginFlag ?? false;
    this.complete = false;
    this.failed = false;
    this.winActive = false;
    this.winCounter = -1;
    this.deathCounter = -1;
    this.camera = { x: 0, y: 0 };
    this.spikesKill = false;
    this.staticBodies = [];
    this.dynamicBodies = {};
    this.joints = [];
    this.particles = [];
    this.dynamicSpriteImages = {};
    this.assetsEnabled = assetsEnabled;
    this.fixedLevelTransitionTiming = fixedLevelTransitionTiming === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;

    this.stageImage = this.assetsEnabled ? new Image() : null;
    if (this.stageImage) {
      this.stageImage.src = levelData.foregroundSvgPath ?? levelData.svgPath;
    }

    this.backgroundImage = this.assetsEnabled ? new Image() : null;
    if (this.backgroundImage) {
      this.backgroundImage.src = levelData.symbols.backGroundMC.imagePath;
    }
    this.backgroundStart = levelData.layout.namedChildren.backGroundMC;
    this.backgroundSymbol = levelData.symbols.backGroundMC;

    this.playerImage = this.assetsEnabled ? new Image() : null;
    if (this.playerImage) {
      this.playerImage.src = "scratch/l1_png/DefineSprite_23_PlayerBox/1.png";
    }

    for (const name of config.dynamicSprites ?? []) {
      if (this.assetsEnabled) {
        const image = new Image();
        image.src = levelData.symbols[name].imagePath;
        this.dynamicSpriteImages[name] = image;
      }
    }

    this.goalFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_68", LEVEL_AIM_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;

    this.checkpointFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_85", CHECKPOINT_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.checkpoints = Object.entries(this.levelData.layout.namedChildren)
      .filter(([name]) => /^checkPoint\d+$/.test(name))
      .sort((a, b) => Number(a[0].replace("checkPoint", "")) - Number(b[0].replace("checkPoint", "")))
      .map(([name, rect], index) => ({
        name,
        index,
        rect,
        currentFrame: 1,
        playing: false,
      }));
    this.checkpointIndex = Math.min(runtimeState?.checkpointIndex ?? 0, Math.max(0, this.checkpoints.length - 1));

    this.goalPosition = this.levelData.layout.namedChildren.levelAim;
    this.goalSymbol = this.levelData.symbols.levelAim;
    this.goalRect = displayBounds(this.goalPosition, this.goalSymbol, "levelAim");
    this.audioEnabled = audioEnabled;
    this.levelSound = this.audioEnabled && this.assetsEnabled ? new Audio("decomp/export/sounds/12_LevelSound.mp3") : null;
    this.hazardRects = (config.hazards ?? [])
      .map((name) => this.levelData.layout.namedChildren[name])
      .filter(Boolean);

    this.createBodies();
    this.configureLevel();
    this.resetPlayer();
  }

  setSpikesKill(enabled) {
    this.spikesKill = enabled;
  }

  setFixedLevelTransitionTiming(enabled) {
    this.fixedLevelTransitionTiming = enabled === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;
  }

  configureLevel() {}

  serializeState() {
    return {
      checkpointIndex: this.checkpointIndex,
      beginFlag: this.beginFlag,
    };
  }

  createBodies() {
    for (const entry of this.levelData.bodies) {
      const layout = this.levelData.layout.namedChildren[entry.name];
      if (!layout) {
        continue;
      }
      const bodyDef = new b2BodyDef();
      bodyDef.type = entry.density > 0 ? b2Body.b2_dynamicBody : b2Body.b2_staticBody;
      bodyDef.position.Set(layout.x / PHYS_SCALE, layout.y / PHYS_SCALE);
      bodyDef.angle = ((layout.rotation ?? 0) * Math.PI) / 180;
      bodyDef.userData = { __kind: "body", name: entry.name };
      const body = this.world.CreateBody(bodyDef);
      if (entry.shape === "Circle") {
        const fixtureDef = new b2FixtureDef();
        fixtureDef.density = entry.density;
        fixtureDef.friction = entry.friction;
        fixtureDef.restitution = entry.restitution;
        fixtureDef.shape = new b2CircleShape((entry.data[0] / 2) / PHYS_SCALE);
        fixtureDef.shape.SetLocalPosition(new b2Vec2((entry.data[0] / 2) / PHYS_SCALE, (entry.data[0] / 2) / PHYS_SCALE));
        body.CreateFixture(fixtureDef);
      } else {
        for (const polygon of entry.data) {
          const fixtureDef = new b2FixtureDef();
          fixtureDef.density = entry.density;
          fixtureDef.friction = entry.friction;
          fixtureDef.restitution = entry.restitution;
          fixtureDef.shape = new b2PolygonShape();
          const vertices = polygon.map(([x, y]) => new b2Vec2(x / PHYS_SCALE, y / PHYS_SCALE));
          fixtureDef.shape.SetAsArray(vertices, vertices.length);
          body.CreateFixture(fixtureDef);
        }
      }
      if (entry.density > 0) {
        body.SetAwake(true);
        this.dynamicBodies[entry.name] = body;
      } else {
        this.staticBodies.push(body);
      }
    }
  }

  resetPlayer() {
    if (this.player?.alive) {
      this.player.kill(this.world);
    }
    const checkpoint = this.checkpoints[this.checkpointIndex] ?? this.checkpoints[0];
    this.player = new PlayerBox(this.world, checkpoint.rect.x, checkpoint.rect.y);
    this.player.body.SetUserData({ __kind: "player", ref: this.player });
    this.camera.x = -checkpoint.rect.x + 275;
    this.camera.y = -checkpoint.rect.y + 200;
    this.particles = [];
    this.deathCounter = -1;
    this.failed = false;
    this.winActive = false;
    this.complete = false;
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;
    for (const checkpointState of this.checkpoints) {
      checkpointState.currentFrame = checkpointState.index < this.checkpointIndex ? CHECKPOINT_FRAME_COUNT : 1;
      checkpointState.playing = false;
    }
  }

  getBodyAtPoint(x, y, includeStatic = false) {
    const point = new b2Vec2(x, y);
    const aabb = new b2AABB();
    aabb.lowerBound.Set(x - 0.001, y - 0.001);
    aabb.upperBound.Set(x + 0.001, y + 0.001);
    const fixtures = [];
    const broadPhase = this.world?.m_broadPhase;
    const count = typeof broadPhase?.QueryAABB === "function"
      ? broadPhase.QueryAABB(aabb, fixtures, 10)
      : 0;
    for (let index = 0; index < count; index += 1) {
      const fixture = fixtures[index];
      const body = fixture?.GetBody?.() ?? fixture?.m_body ?? null;
      const shape = fixture?.GetShape?.() ?? fixture?.m_shape ?? fixture;
      if (!body || !shape) {
        continue;
      }
      if ((body.GetType() !== b2Body.b2_staticBody || includeStatic) && shape.TestPoint(body.GetTransform(), point)) {
        return body;
      }
    }
    return null;
  }

  getPlayerRect() {
    return playerFlagBounds(this.player);
  }

  playerOverlapsRect(rect) {
    return Boolean(rect && this.player?.isLive?.() && rectsOverlap(this.getPlayerRect(), rect));
  }

  startDeath() {
    if (!this.player?.isLive() || this.deathCounter >= 0) {
      return;
    }
    const velocity = this.player.body.GetLinearVelocity();
    this.deathCounter = DEATH_DELAY_FRAMES;
    for (let index = 0; index < 8; index += 1) {
      this.particles.push({
        x: this.player.x - 4 + 5 * Math.random() - 5 * Math.random(),
        y: this.player.y - 4 + 5 * Math.random() - 5 * Math.random(),
        vx: velocity.x / 3 * PHYS_SCALE + 7 * Math.cos(index * 2 * Math.PI / 8),
        vy: velocity.y / 3 * PHYS_SCALE + 7 * Math.sin(index * 2 * Math.PI / 8),
      });
    }
    this.player.kill(this.world);
  }

  startWin() {
    if (this.goalPlaying || this.complete) {
      return;
    }
    this.goalPlaying = true;
    this.winActive = true;
    this.winCounter = this.winDelayFrames;
    this.clock.Freeze(this.winDelayFrames);
    this.player.body.SetLinearDamping(3);
    this.player.body.SetAngularDamping(3);
    if (this.levelSound) {
      try {
        this.levelSound.currentTime = 0;
        void this.levelSound.play();
      } catch {
        // Browser autoplay restrictions can block this.
      }
    }
  }

  updateAnimations() {
    for (const checkpointState of this.checkpoints) {
      if (checkpointState.playing && checkpointState.currentFrame < CHECKPOINT_FRAME_COUNT) {
        checkpointState.currentFrame += 1;
      } else if (checkpointState.playing) {
        checkpointState.playing = false;
      }
    }
    if (this.goalPlaying && this.goalCurrentFrame < LEVEL_AIM_FRAME_COUNT) {
      this.goalCurrentFrame += 1;
    }
    for (const particle of this.particles) {
      particle.vy += 0.35;
      particle.x += particle.vx * FIXED_TIMESTEP;
      particle.y += particle.vy * FIXED_TIMESTEP;
    }
  }

  updateCamera() {
    const targetCameraX = -this.player.x + 275;
    const targetCameraY = -this.player.y + 200;
    this.camera.x += (targetCameraX - this.camera.x) * CAMERA_EASE;
    this.camera.y += (targetCameraY - this.camera.y) * CAMERA_EASE;
  }

  handleControls(input) {
    if (this.beginFlag && RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.clock?.unpause?.();
      this.beginFlag = false;
    }
    const position = this.player.body.GetPosition();
    const centerSupport = Boolean(this.getBodyAtPoint(position.x, position.y + 0.4, true));
    const leftSupport = Boolean(this.getBodyAtPoint(position.x - 0.2, position.y + 0.38, true));
    const rightSupport = Boolean(this.getBodyAtPoint(position.x + 0.2, position.y + 0.38, true));
    const hasGroundSupport = centerSupport || leftSupport || rightSupport;
    const velocity = this.player.body.GetLinearVelocity();

    if (LEFT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x > -5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.5, velocity.y));
        }
      } else if (velocity.x > -2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.25, velocity.y));
      }
    }

    if (RIGHT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x < 5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.5, velocity.y));
        }
      } else if (velocity.x < 2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.25, velocity.y));
      }
    }

    if (UP_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (this.contactListener.playerContactBodies.length > 0 && hasGroundSupport) {
        this.player.body.ApplyForce(new b2Vec2(0, -65), this.player.body.GetWorldCenter());
      } else if (velocity.y < 0) {
        this.player.body.ApplyForce(new b2Vec2(0, -1), this.player.body.GetWorldCenter());
      }
    }
  }

  updateLevelBodies() {}

  handleBaseInteractions() {
    for (const checkpointState of this.checkpoints) {
      if (this.playerOverlapsRect(checkpointState.rect) && checkpointState.currentFrame === 1) {
        if (checkpointState.index > this.checkpointIndex) {
          this.checkpointIndex = checkpointState.index;
        }
        checkpointState.playing = true;
      }
    }
    if (this.playerOverlapsRect(this.goalRect) && this.goalCurrentFrame === 1) {
      this.startWin();
    }
    for (const hazardRect of this.hazardRects) {
      if (this.spikesKill && this.player.hitTestObjectControlPoints(hazardRect)) {
        this.startDeath();
      }
    }
    if (this.player.y > this.config.deathY) {
      this.startDeath();
    }
  }

  update(input) {
    this.updateAnimations();

    if (this.deathCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.world.ClearForces();
      this.updateLevelBodies();
      this.clock.update();
      this.deathCounter -= 1;
      if (this.deathCounter <= 0) {
        this.failed = true;
      }
      return;
    }

    if (this.winCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.world.ClearForces();
      this.updateLevelBodies();
      this.clock.update();
      this.updateCamera();
      this.winCounter -= 1;
      if (this.winCounter <= 0) {
        this.winActive = false;
        this.complete = true;
      }
      return;
    }

    this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
    this.world.ClearForces();
    this.handleControls(input);
    this.handleBaseInteractions();
    this.clock.update();
    this.updateCamera();
    this.updateLevelBodies();
  }

  renderBackground(ctx) {
    if (!this.backgroundImage.complete) {
      return;
    }
    drawSymbol(
      ctx,
      this.backgroundImage,
      this.backgroundSymbol,
      this.backgroundStart.x - this.camera.x / 10,
      this.backgroundStart.y - this.camera.y / 10,
      0,
    );
  }

  renderForeground(ctx) {
    if (this.stageImage.complete) {
      drawStageImage(ctx, this.stageImage, {
        ...this.levelData,
        imagePath: this.levelData.foregroundSvgPath ?? this.levelData.svgPath,
      });
    }
  }

  renderCheckpoints(ctx) {
    for (const checkpointState of this.checkpoints) {
      const frame = this.checkpointFrames[checkpointState.currentFrame - 1];
      if (frame?.complete) {
        drawAnimatedFrame(
          ctx,
          frame,
          this.levelData.symbols[checkpointState.name],
          checkpointState.rect.x,
          checkpointState.rect.y,
        );
      }
    }
  }

  renderGoal(ctx) {
    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    if (goalFrame?.complete) {
      drawAnimatedFrame(ctx, goalFrame, this.goalSymbol, this.goalPosition.x, this.goalPosition.y);
    }
  }

  renderDynamicBodies(ctx) {
    for (const name of this.config.dynamicSprites ?? []) {
      const body = this.dynamicBodies[name];
      if (!body) {
        continue;
      }
      const position = body.GetPosition();
      drawSymbol(
        ctx,
        this.dynamicSpriteImages[name],
        this.levelData.symbols[name],
        position.x * PHYS_SCALE,
        position.y * PHYS_SCALE,
        body.GetAngle(),
      );
    }
  }

  renderPlayer(ctx) {
    if (!this.player?.isLive()) {
      return;
    }
    ctx.save();
    ctx.translate(this.player.x, this.player.y);
    ctx.rotate(this.player.body.GetAngle());
    if (this.playerImage.complete) {
      ctx.drawImage(this.playerImage, -10.5, -10.5);
    }
    ctx.restore();
  }

  renderParticles(ctx) {
    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  renderJoints(ctx) {
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1;
    for (const joint of this.joints) {
      if (!joint.GetAnchorA || !joint.GetAnchorB) {
        continue;
      }
      const a = joint.GetAnchorA();
      const b = joint.GetAnchorB();
      ctx.beginPath();
      ctx.moveTo(a.x * PHYS_SCALE, a.y * PHYS_SCALE);
      ctx.lineTo(b.x * PHYS_SCALE, b.y * PHYS_SCALE);
      ctx.stroke();
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);
    this.renderBackground(ctx);
    ctx.translate(this.camera.x, this.camera.y);
    this.renderForeground(ctx);
    this.renderCheckpoints(ctx);
    this.renderGoal(ctx);
    this.renderDynamicBodies(ctx);
    this.renderPlayer(ctx);
    this.renderParticles(ctx);
    this.renderJoints(ctx);
    ctx.restore();
  }
}
